/**
 * Document rows, as on WO create: one row per document type (a service's required document, or a type picked from
 * "Add document", which adds an optional row - the upload is done from the row). A row with files lists each one with
 * View / Remove and an "Uploaded" chip plus Add file; an empty row offers Capture and Upload. Files can also be
 * dropped or pasted on a row.
 *
 * The parent stages the files (onFiles(files, row)) and removes them (onRemove(docId)); pre-filling is the parent's.
 */

import React, { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel, ListSubheader, MenuItem, Select, Typography,
} from '@mui/material';
import { CropFree as CaptureIcon } from '@mui/icons-material';
import CaptureDialog from '../capture/CaptureDialog';
import { getDocIcon } from '../../utils/docTypeIcons';
import { splitDocTypes } from '../../utils/docTypeGroups';
import { filesFromEvent } from '../../utils/dropPasteFiles';

const xs = '0.75rem';
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.doc,.docx';

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const WorkOrderDocumentRows = ({
  rows = [],             // [{ code, label, required }]
  documents = [],        // staged: [{ id, documentType, file, fileName, fileSize, expiryDate, extracting }]
  documentTypes = [],    // all TC types, for the "Add document" dropdown
  onAddType,             // (code) => void
  onFiles,               // (files, { code, label }) => void
  onRemove,              // (docId) => void
  captureName = '',      // applicant name, for captured file names
  disabled = false,
}) => {
  const [dragTarget, setDragTarget] = useState(null);
  const [captureTarget, setCaptureTarget] = useState(null);

  const rowCodes = new Set(rows.map(r => r.code));
  const addable = documentTypes.filter(dt => !rowCodes.has(dt.code || dt.id));

  const pick = (row) => (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length) onFiles(files, row);
  };
  const drop = (row) => (e) => {
    setDragTarget(null);
    const files = filesFromEvent(e);
    if (files.length) { e.preventDefault(); onFiles(files, row); }
  };

  const typeMenu = () => {
    const { primary, secondary } = splitDocTypes(addable);
    const item = (dt) => (
      <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ pl: 3, fontSize: '0.8125rem' }}>
        {dt.label || dt.name || dt.code}
      </MenuItem>
    );
    const out = [];
    if (primary.length) {
      out.push(<ListSubheader key="_p" sx={{ fontWeight: 700, fontSize: xs, color: 'primary.main', lineHeight: '28px' }}>Primary Documents</ListSubheader>);
      primary.forEach(dt => out.push(item(dt)));
    }
    if (secondary.length) {
      out.push(<ListSubheader key="_s" sx={{ fontWeight: 700, fontSize: xs, color: 'text.secondary', lineHeight: '28px' }}>Secondary Documents</ListSubheader>);
      secondary.forEach(dt => out.push(item(dt)));
    }
    return out;
  };

  return (
    <Box sx={disabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
      {rows.map((row) => {
        const files = documents.filter(d => d.documentType === row.code);
        const uploaded = files.length > 0;
        return (
          <Box
            key={row.code}
            tabIndex={0}
            onPaste={drop(row)}
            onDrop={drop(row)}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => { e.preventDefault(); setDragTarget(row.code); }}
            onDragLeave={() => setDragTarget(null)}
            title="Drop a file here, or click and paste a copied image (Ctrl/⌘+V)"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
              borderBottom: '1px solid', borderColor: 'divider',
              outline: dragTarget === row.code ? '2px dashed' : 'none', outlineColor: 'primary.main', outlineOffset: -2,
              bgcolor: dragTarget === row.code ? 'action.hover' : 'transparent',
            }}
          >
            <Typography component="span" sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{getDocIcon(row.code)}</Typography>
            <Typography noWrap sx={{ fontSize: xs, fontWeight: 500, width: 140, flexShrink: 0 }}>
              {row.label}
              {row.required && !uploaded && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
            </Typography>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              {files.map((doc) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: xs, color: 'text.secondary', minWidth: 0 }}>
                    {doc.fileName}
                    <Typography component="span" sx={{ fontSize: xs, color: 'text.disabled', ml: 0.5 }}>
                      · {formatFileSize(doc.fileSize)}{doc.expiryDate ? ` · Exp ${doc.expiryDate}` : ''}
                    </Typography>
                  </Typography>
                  {doc.extracting && <CircularProgress size={12} sx={{ flexShrink: 0 }} />}
                  {doc.file && (
                    <Button size="small" onClick={() => window.open(URL.createObjectURL(doc.file), '_blank')}
                      sx={{ fontSize: xs, textTransform: 'none', minWidth: 0, p: 0.25, color: 'text.secondary', flexShrink: 0 }}>
                      View
                    </Button>
                  )}
                  <Button size="small" onClick={() => onRemove(doc.id)}
                    sx={{ fontSize: xs, textTransform: 'none', minWidth: 0, p: 0.25, color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'error.main' } }}>
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: 160, justifyContent: 'flex-end' }}>
              {uploaded ? (
                <>
                  <Chip label={files.length > 1 ? `${files.length} uploaded` : 'Uploaded'} size="small" color="success"
                    sx={{ fontSize: '0.6rem', height: 18 }} />
                  <Button size="small" component="label" sx={{ fontSize: xs, textTransform: 'none', minWidth: 0, px: 1 }}>
                    Add file
                    <input type="file" hidden multiple accept={ACCEPT} onChange={pick(row)} />
                  </Button>
                </>
              ) : (
                <>
                  <IconButton size="small" title="Capture from screen" onClick={() => setCaptureTarget(row)} sx={{ color: 'text.secondary' }}>
                    <CaptureIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Button size="small" component="label" sx={{ fontSize: xs, textTransform: 'none', minWidth: 0, px: 1 }}>
                    Upload
                    <input type="file" hidden multiple accept={ACCEPT} onChange={pick(row)} />
                  </Button>
                </>
              )}
            </Box>
          </Box>
        );
      })}

      {addable.length > 0 && onAddType && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1 }}>
          <Typography component="span" sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>➕</Typography>
          <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel sx={{ fontSize: xs }}>Add document...</InputLabel>
            <Select value="" label="Add document..." onChange={(e) => onAddType(e.target.value)} sx={{ fontSize: xs }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
              {typeMenu()}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: xs, color: 'text.disabled', flexShrink: 0 }}>adds it to the list above</Typography>
        </Box>
      )}

      <CaptureDialog
        open={!!captureTarget}
        onClose={() => setCaptureTarget(null)}
        documentType={captureTarget?.label}
        onCapture={(file) => {
          if (!captureTarget) return;
          const nameSlug = (captureName || 'unknown').replace(/\s+/g, '-').toUpperCase();
          const typeSlug = (captureTarget.label || captureTarget.code).replace(/\s+/g, '-').toLowerCase();
          const fileName = `${nameSlug}_${typeSlug}_${new Date().toISOString().slice(0, 10)}.jpg`;
          onFiles([new File([file], fileName, { type: file.type })], captureTarget);
        }}
      />
    </Box>
  );
};

export default WorkOrderDocumentRows;
